import { convertDateToTimeZone, DateFormatter } from 'infrastructure/functions';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { THRESHOLD_UNITS, Timezones } from 'infrastructure/enums';

import { generateWeightMessage } from '../blurbs';
import { getNursingInterventionValues } from '../helpers';

import type { IUseChfWeightProps } from '../types';
import type { IWeightValues } from 'components/molecules/rpm-alert-assessment/types';

export const useWeightAssessment = ({
  activityId,
  activityDate,
  values,
  patientGuid,
  addComment,
  closeModal,
  handleSubmit,
}: IUseChfWeightProps) => {
  const { patientInfo } = usePatientInfo({
    patientGuid,
  });

  const initialValues: IWeightValues = {
    // Q1
    patient: undefined,
    other: null,
    relationship: null,
    // Q2
    AOC: null,
    // Q3
    last: null,
    next: null,
    // Q4
    isOneMoreReading: null,
    bounds: null,
    repeatReadingWas: null,
    // Q5
    isAgreeWeightDiscuss: null,
    // Q6
    isWeightChangeIntentional: null,
    // Q7
    isTakeWeightLossMedications: null,
    weightLossMedicationsOther: null,
    // Q8
    deniedFactors: [],
    confirmedFactors: [],
    // Q9
    weightAssessment: [],
    weightAssessmentOther: null,
    // Q10
    emergencyCare: null,
    nursingIntervention: [],
    notEscalated: [],
    interventionDetails: null,
    escalationToClinic: [],
    // Q11
    verbalized: null,
  };

  const reading = `${values.weight} ${THRESHOLD_UNITS.Weight}`;
  const timeZone =
    patientInfo?.loginInfo?.timezone || Timezones['America/Chicago'];

  const dataInPatientTimeZone = convertDateToTimeZone(
    new Date(Number(activityDate)),
    Timezones[timeZone],
  );
  const dateOfReading = DateFormatter({
    date: dataInPatientTimeZone,
  });

  const preparedData = (data: IWeightValues) => {
    const isSpeakWithPatient = data.patient === 'Patient';
    const lastDate = data.last
      ? DateFormatter({ date: new Date(data.last) })
      : null;
    const nextDate = data.next
      ? DateFormatter({ date: new Date(data.next) })
      : null;
    const nursingInterventionValues = getNursingInterventionValues(
      data?.nursingIntervention,
    );

    return {
      // Did you speak to patient?
      Q1: {
        reading,
        dateOfReading,
        patient: data.patient,
        relationship: data.relationship,
        speakWithPatient: isSpeakWithPatient,
        speakWithOther: data.other,
      },
      // Mental Status Assessment
      Q2: {
        AOC: data.AOC,
      },
      // Appointments
      Q3: {
        last: lastDate,
        next: nextDate,
      },
      //
      Q4: {
        isOneMoreReading: data.isOneMoreReading,
        bounds: data.bounds,
        repeatReadingWas: data.repeatReadingWas,
      },
      Q5: {
        isAgreeWeightDiscuss: data.isAgreeWeightDiscuss,
      },
      Q6: {
        isWeightChangeIntentional: data.isWeightChangeIntentional,
      },
      Q7: {
        isTakeWeightLossMedications: data.isTakeWeightLossMedications,
        weightLossMedicationsOther: data.weightLossMedicationsOther,
      },
      // Factors Assessment
      Q8: {
        confirmedFactors: data.confirmedFactors,
        deniedFactors: data.deniedFactors,
      },
      // Weight Assessment
      Q9: {
        weightAssessment: data.weightAssessment,
        weightAssessmentOther: data.weightAssessmentOther,
      },
      // Nursing Intervention
      Q10: {
        ...nursingInterventionValues,
        notEscalated: data.notEscalated,
        interventionDetails: data.interventionDetails,
        escalationToClinic: data.escalationToClinic,
      },
      Q11: {
        verbalized: data.verbalized,
      },
    };
  };

  const onSaveForm = (data: IWeightValues) => {
    const preparedValues = preparedData(data);
    const message = generateWeightMessage(preparedValues);

    handleSubmit(message);
    addComment(activityId);
    closeModal();
  };

  return {
    initialValues,
    onSaveForm,
  };
};
